import React, { useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Alert,
    CircularProgress,
} from "@mui/material";
import { Lock, Visibility, VisibilityOff } from "@mui/icons-material";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";
import useNotification from "./Notification";
import { useAuth } from "../../context/AuthContext";
import Cookies from "js-cookie";

const ResetPassword = () => {
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const notify = useNotification();
    const { logout } = useAuth();

    const validateForm = () => {
        const newErrors = {};

        if (!oldPassword.trim()) {
            newErrors.oldPassword = "Old password is required";
        }

        if (!newPassword.trim()) {
            newErrors.newPassword = "New password is required";
        } else if (newPassword.length < 5) {
            newErrors.newPassword = "New password must be at least 5 characters";
        }

        if (!confirmPassword.trim()) {
            newErrors.confirmPassword = "Please confirm your new password";
        } else if (newPassword !== confirmPassword) {
            newErrors.confirmPassword = "Passwords do not match";
        }

        if (oldPassword === newPassword) {
            newErrors.newPassword = "New password must be different from old password";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const clearAllCookies = () => {
        // Get all cookies and remove them
        const allCookies = Cookies.get();
        Object.keys(allCookies).forEach((cookieName) => {
            Cookies.remove(cookieName);
        });

        // Also clear localStorage
        localStorage.clear();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setErrors({});

        try {
            const response = await axiosInstance.post(
                `${base_url}/account-config/reset-password`,
                {
                    old_password: oldPassword,
                    new_password: newPassword,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                }
            );

            if (response.data?.success) {
                notify("Password changed successfully! Logging out...", "success");

                // Wait a moment to show the success message
                setTimeout(() => {
                    // Clear all cookies
                    clearAllCookies();

                    // Logout user
                    logout();

                    // Redirect to home (which will show login since user is logged out)
                    window.location.href = "/";
                }, 1500);
            } else {
                notify(
                    response.data?.message || "Failed to reset password",
                    "error"
                );
            }
        } catch (error) {
            console.error("Error resetting password:", error);

            // Handle different error responses
            if (error.response?.data) {
                const errorData = error.response.data;

                // Handle validation errors
                if (errorData.errors && typeof errorData.errors === "object") {
                    const validationErrors = {};
                    Object.keys(errorData.errors).forEach((field) => {
                        if (Array.isArray(errorData.errors[field])) {
                            validationErrors[field] = errorData.errors[field][0];
                        } else {
                            validationErrors[field] = errorData.errors[field];
                        }
                    });
                    setErrors(validationErrors);
                }

                // Show error message
                const errorMessage =
                    errorData.message ||
                    errorData.error ||
                    "Failed to reset password";
                notify(errorMessage, "error");
            } else {
                notify("Network error. Please try again.", "error");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "calc(100vh - 200px)",
                p: 3,
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    maxWidth: 500,
                    width: "100%",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Lock sx={{ fontSize: 40, color: "primary.main", mr: 2 }} />
                    <Typography variant="h5" fontWeight="bold">
                        Change Password
                    </Typography>
                </Box>

                <Alert severity="info" sx={{ mb: 3 }}>
                    After successfully changing your password, you will be logged out
                    and redirected to the login page.
                </Alert>

                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        label="Old Password"
                        type={showOldPassword ? "text" : "password"}
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        error={!!errors.oldPassword}
                        helperText={errors.oldPassword}
                        sx={{ mb: 2 }}
                        required
                        InputProps={{
                            endAdornment: (
                                <Button
                                    onClick={() => setShowOldPassword(!showOldPassword)}
                                    sx={{ minWidth: "auto", p: 1 }}
                                >
                                    {showOldPassword ? (
                                        <VisibilityOff />
                                    ) : (
                                        <Visibility />
                                    )}
                                </Button>
                            ),
                        }}
                    />

                    <TextField
                        fullWidth
                        label="New Password"
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        error={!!errors.newPassword}
                        helperText={errors.newPassword || "Minimum 5 characters"}
                        sx={{ mb: 2 }}
                        required
                        InputProps={{
                            endAdornment: (
                                <Button
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    sx={{ minWidth: "auto", p: 1 }}
                                >
                                    {showNewPassword ? (
                                        <VisibilityOff />
                                    ) : (
                                        <Visibility />
                                    )}
                                </Button>
                            ),
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Confirm New Password"
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword}
                        sx={{ mb: 3 }}
                        required
                        InputProps={{
                            endAdornment: (
                                <Button
                                    onClick={() =>
                                        setShowConfirmPassword(!showConfirmPassword)
                                    }
                                    sx={{ minWidth: "auto", p: 1 }}
                                >
                                    {showConfirmPassword ? (
                                        <VisibilityOff />
                                    ) : (
                                        <Visibility />
                                    )}
                                </Button>
                            ),
                        }}
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={loading}
                        sx={{ py: 1.5 }}
                    >
                        {loading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            "Change Password"
                        )}
                    </Button>
                </form>
            </Paper>
        </Box>
    );
};

export default ResetPassword;

